import axios from 'axios'
import { message } from 'ant-design-vue'
import store from '@/store'
import router from '@/router'
import errorHandler from '@/utils/error-report/errorHandler.js'
import errorReportConfig from '@/config/errorReport.js'
import { buildApiErrorAdditional } from '@/utils/error-report/apiReportPayload.js'

// 根据环境变量判断是否使用代理
// 如果启用代理，使用相对路径 '/api'，请求会通过 vue.config.js 中的代理转发
// 如果未启用代理，直接使用 VUE_APP_BASE_API（需要自己包含完整路径，如 /api）
const useProxy = process.env.VUE_APP_USE_PROXY === 'true'
// 回退示例正式站：https://cb.cibeiai.com；测试站：https://cibeicsy.2kang.cn
// const baseURL = useProxy ? '/api' : (process.env.VUE_APP_BASE_API || 'https://cb.cibeiai.com')
const baseURL = useProxy ? '/api' : process.env.VUE_APP_BASE_API

const _isErrorReportUrl = (url = '') => {
  const p = errorReportConfig.reportUrl || '/system/error/report'
  return String(url).includes(p) || String(url).includes('error/report')
}

function _reportApiFailure (config, err, extras = {}) {
  const url = extras.url || config?.url || ''
  if (!url || _isErrorReportUrl(url)) return
  const additional = buildApiErrorAdditional(config, { url, ...extras })
  errorHandler.handleApiError(url, err, additional).catch(() => {})
}

// 创建 axios 实例
const service = axios.create({
  baseURL,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 从 store 获取 token
    const token = store.getters['user/token']
    if (token) {
      config.headers.Authorization = `${token}`
    }
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 特殊接口兼容：/course/vip/create_order code = 2 表示领取成功，不算错误
    const url = (response.config && response.config.url) || ''
    if (url.indexOf('/course/vip/create_order') !== -1 && res.code === 2) {
      return res
    }

    // code: 1 表示成功（配置接口等），code: 200 也表示成功（普通接口）
    // code: 0 或其他值表示失败
    if (res.code !== undefined && res.code !== 1 && res.code !== 200) {
      const errorMsg = res.msg || res.message || '请求失败'
      message.error(errorMsg)

      // 401: 未登录或 token 过期
      if (res.code === 401) {
        store.dispatch('user/logout')
        router.push('/login')
      }

      const err = new Error(errorMsg)
      err.statusCode = response.status
      _reportApiFailure(response.config, err, {
        url,
        statusCode: response.status,
        responseData: res,
        method: response.config?.method,
        businessCode: res.code,
      })

      return Promise.reject(err)
    } else {
      return res
    }
  },
  error => {
    console.error('响应错误:', error)

    let errorMessage = '请求失败'

    if (error.response) {
      switch (error.response.status) {
        case 401:
          errorMessage = '未授权，请重新登录'
          store.dispatch('user/logout')
          router.push('/login')
          break
        case 403:
          errorMessage = '拒绝访问'
          break
        case 404:
          errorMessage = '请求错误，未找到该资源'
          break
        case 500:
          errorMessage = '服务器错误'
          break
        default:
          errorMessage = `连接错误${error.response.status}`
      }
    } else if (error.request) {
      errorMessage = '网络连接失败'
    } else {
      errorMessage = error.message
    }

    const cfg = error.config || {}
    const url = cfg.url || ''
    const err = error instanceof Error ? error : new Error(errorMessage)
    err.statusCode = error.response?.status
    if (!err.message) err.message = errorMessage
    _reportApiFailure(cfg, err, {
      url,
      statusCode: error.response?.status,
      responseData: error.response?.data,
      method: cfg.method,
    })

    message.error(errorMessage)
    return Promise.reject(error)
  }
)

export default service
